import FormDatePicker from 'components/form/FormDatePicker';
import { addDays, addMonths, format } from 'date-fns';
import { useState, type JSX } from 'react';
import { submit } from 'utils/app';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm, useWatch } from 'react-hook-form';
import ReactModal from 'react-modal';
/**
 * レポート期間設定モーダル
 */
export default function WsModalReportTerm(props: {
  setOpen: (open: boolean) => void;
  open: boolean;
  reportDateFrom: string;
  reportDateTo: string;
  onSubmit: (attr: { reportDateFrom: string; reportDateTo: string }) => void;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  type FormStateType = {
    reportTerm: ReportTermType;
    reportTermCustomDateFrom: Date;
    reportTermCustomDateTo: Date;
  };

  type ReportTermType = '1month' | '3month' | '6month' | 'custom';
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();

  const [processing, setProcessing] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [reportCustomTermError, setReportCustomTermError] = useState(null);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormStateType>();

  const reportTermValue = useWatch({
    control: control,
    name: 'reportTerm',
  });

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * モーダルを閉じた時の処理
   */
  const onClose = () => {
    setReportCustomTermError(null);
    setInitialized(false);

    props.setOpen(false);
  };

  /**
   * モーダルが開いた後の処理
   */
  const onAfterOpen = () => {
    setValue('reportTerm', _getReportTerm(props.reportDateFrom, props.reportDateTo));
    setValue('reportTermCustomDateFrom', new Date(props.reportDateFrom));
    setValue('reportTermCustomDateTo', new Date(props.reportDateTo));

    setReportCustomTermError(null);
    setInitialized(true);
  };

  /**
   * 設定ボタン押下時の処理
   */
  const onSubmit = () =>
    submit(
      processing,
      setProcessing,
      handleSubmit((data: FormStateType) => {
        try {
          // カスタム期間の入力チェック
          if (data.reportTerm == 'custom') {
            // 開始日と終了日の必須検証
            if (!data.reportTermCustomDateFrom || !data.reportTermCustomDateTo) {
              setReportCustomTermError('期間を設定してください。');
              return;
            }
            // 開始日と終了日のFromTo検証
            if (data.reportTermCustomDateFrom > data.reportTermCustomDateTo) {
              setReportCustomTermError('開始日は終了日より前の日付を設定してください。');
              return;
            }
            // 期間が16か月であるかの検証
            if (addMonths(data.reportTermCustomDateFrom, 16) < data.reportTermCustomDateTo) {
              setReportCustomTermError('16か月以内で設定してください。');
              return;
            }
          }

          // デフォルトの表示期間
          const toDate = addDays(new Date(), -1);

          if (data.reportTerm == '1month') {
            props.onSubmit({
              reportDateFrom: format(addMonths(toDate, -1), 'yyyy/MM/dd'),
              reportDateTo: format(toDate, 'yyyy/MM/dd'),
            });
          } else if (data.reportTerm == '3month') {
            props.onSubmit({
              reportDateFrom: format(addMonths(toDate, -3), 'yyyy/MM/dd'),
              reportDateTo: format(toDate, 'yyyy/MM/dd'),
            });
          } else if (data.reportTerm == '6month') {
            props.onSubmit({
              reportDateFrom: format(addMonths(toDate, -6), 'yyyy/MM/dd'),
              reportDateTo: format(toDate, 'yyyy/MM/dd'),
            });
          } else if (data.reportTerm == 'custom') {
            props.onSubmit({
              reportDateFrom: format(data.reportTermCustomDateFrom, 'yyyy/MM/dd'),
              reportDateTo: format(data.reportTermCustomDateTo, 'yyyy/MM/dd'),
            });
          }

          // モーダルを閉じる
          onClose();
        } catch (e) {
          showBoundary(e);
        }
      })
    );

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 集計期間から現在設定している集計期間の種類を取得します。
   */
  const _getReportTerm = (reportDateFrom: string, reportDateTo: string): ReportTermType => {
    const defaultToDate = addDays(new Date(), -1);

    if (format(defaultToDate, 'yyyy/MM/dd') != reportDateTo) {
      return 'custom';
    }

    if (format(addMonths(defaultToDate, -1), 'yyyy/MM/dd') == reportDateFrom) {
      return '1month';
    }
    if (format(addMonths(defaultToDate, -3), 'yyyy/MM/dd') == reportDateFrom) {
      return '3month';
    }
    if (format(addMonths(defaultToDate, -6), 'yyyy/MM/dd') == reportDateFrom) {
      return '6month';
    }
    return 'custom';
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <ReactModal
        isOpen={props.open}
        onAfterOpen={onAfterOpen}
        onRequestClose={onClose}
        style={{
          overlay: {
            zIndex: 2000,
            backgroundColor: 'rgba(30, 30, 30,0.5)',
          },
          content: {
            maxWidth: '500px',
          },
        }}
        className="ReactModal__Content__Custom"
        ariaHideApp={false}
      >
        {initialized && (
          <div
            id="settings-modal"
            className="modal2 modal-content rounded-4 "
            style={{ maxWidth: 'none' }}
          >
            <div className="modal-header border-bottom-0">
              <button onClick={onClose} type="button" className="btn-close btn-circle-close">
                <img src="/img/icon/multiply.svg" alt="x" />
              </button>
            </div>
            <div className="modal-body">
              <h3 className="modal-title text-center mt-5" id="settings-modal-label">
                レポート集計期間の変更
              </h3>

              <div className="row justify-content-center align-items-center pt-3">
                <div className="col-6 col-md-6">
                  <label className="form-check-label">
                    <input
                      className="form-check-input"
                      type="radio"
                      value={'1month'}
                      {...register('reportTerm')}
                      defaultChecked={'1month' === getValues('reportTerm')}
                    />
                    <span className="ms-1">１か月</span>
                  </label>
                </div>
              </div>
              <div className="row justify-content-center align-items-center pt-3">
                <div className="col-6 col-md-6">
                  <label className="form-check-label">
                    <input
                      className="form-check-input"
                      type="radio"
                      value={'3month'}
                      {...register('reportTerm')}
                      defaultChecked={'3month' === getValues('reportTerm')}
                    />
                    <span className="ms-1">３か月</span>
                  </label>
                </div>
              </div>
              <div className="row justify-content-center align-items-center pt-3">
                <div className="col-6 col-md-6">
                  <label className="form-check-label">
                    <input
                      className="form-check-input"
                      type="radio"
                      value={'6month'}
                      {...register('reportTerm')}
                      defaultChecked={'6month' === getValues('reportTerm')}
                    />
                    <span className="ms-1">６か月</span>
                  </label>
                </div>
              </div>
              <div className="row justify-content-center align-items-center pt-3">
                <div className="col-6 col-md-6">
                  <label className="form-check-label">
                    <input
                      className="form-check-input"
                      type="radio"
                      value={'custom'}
                      {...register('reportTerm')}
                      defaultChecked={'custom' === getValues('reportTerm')}
                    />
                    <span className="ms-1">カスタム</span>
                  </label>
                </div>
              </div>

              <div className="row justify-content-center align-items-center pt-2 ">
                <div className="col-6 col-md-6 d-flex align-items-center">
                  {reportTermValue == 'custom' && (
                    <>
                      <FormDatePicker
                        className="form-controll-small"
                        name="reportTermCustomDateFrom"
                        placeholder="開始日"
                        control={control}
                        errors={errors}
                      />
                      <span className="ms-2 me-2">-</span>
                      <FormDatePicker
                        className="form-controll-small"
                        name="reportTermCustomDateTo"
                        placeholder="終了日"
                        control={control}
                        errors={errors}
                      />
                    </>
                  )}
                </div>
              </div>
              {reportCustomTermError && (
                <div className="row justify-content-center">
                  <div className="col-6 col-md-6">
                    <p className="error">{reportCustomTermError}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer border-top-0 justify-content-center mt-4">
              <button onClick={onClose} type="button" className="btn btn-secondary">
                キャンセル
              </button>
              <button onClick={onSubmit} type="button" className="btn btn-submit">
                設定完了
              </button>
            </div>
          </div>
        )}
      </ReactModal>
    </>
  );
}
